import { useEffect, useMemo } from 'react';
import { unstable_useBlocker as useBlocker } from 'react-router-dom';

import { useUnsavedChangesModal } from '@components/generic-modals';
import { UnsavedChangesPromptModalProps } from '@components/generic-modals/unsaved-changes-modal';

type UseNavigationBlockProps = Omit<UnsavedChangesPromptModalProps, 'onSave'> & {
  onSave(navigationString?: string): void | Promise<void>;
  shouldBlock: boolean;
  allowedPaths?: string[];
  blockBeforeUnload?: boolean;
};

export const useNavigationBlock = ({
  shouldBlock,
  allowedPaths = [],
  title,
  titleIcon,
  description,
  saveText,
  discardText,
  discardButtonVariant,
  isSaveDisabled,
  children,
  onSave,
  onDiscard,
  blockBeforeUnload = true,
}: UseNavigationBlockProps) => {
  const allowedPathsRegex = useMemo(() => {
    const pattern = allowedPaths
      .map((path) => path.replace(/\//g, '\\/').replace(/\{id\}/g, '[^/]+'))
      .join('|');
    return new RegExp(`${pattern}$`);
  }, [allowedPaths]);

  const isAllowedPath = (path: string) => {
    if (allowedPaths.length !== 0) {
      return allowedPathsRegex.test(path);
    }
    return false;
  };

  const blocker = useBlocker(shouldBlock);
  const isBlocked = blocker.state === 'blocked';

  const showUnsavedChangesModal = useUnsavedChangesModal({
    onHide: () => {
      if (isBlocked) {
        // Hate this setTimeout, but don't have a better solution to make
        // the handleAction proceed to finish before resetting
        setTimeout(blocker.reset);
      }
    },
  });

  const handleAction = async (callback?: (path?: string | undefined) => void | Promise<void>) => {
    await callback?.(blocker?.location?.pathname ?? undefined);
    blocker.proceed?.();
  };

  // For navigation within our site
  useEffect(() => {
    if (isBlocked) {
      if (isAllowedPath(blocker.location.pathname)) {
        blocker.proceed?.();
      } else {
        // I did it again... here we need to wait, if the navigating action was
        // triggered by a modal that then closed, such as the command palette
        setTimeout(() => {
          showUnsavedChangesModal({
            onSave: () => handleAction(onSave),
            onDiscard: () => handleAction(onDiscard),
            title,
            titleIcon,
            description,
            saveText,
            discardText,
            discardButtonVariant,
            isSaveDisabled,
            children,
          });
        });
      }
    }
    // I only wanna run this effect if blocker.state changes
    // eslint-disable-next-line
  }, [blocker.state]);

  // For navigation out of our site, or site refresh
  // We can only display a standard browser response to that (cancel / continue)
  useEffect(() => {
    const removeWindowBlock = () => {
      window.onbeforeunload = null;
    };

    if (shouldBlock && blockBeforeUnload) {
      window.onbeforeunload = (event) => {
        event.preventDefault();

        return false;
      };
    } else {
      removeWindowBlock();
    }

    return removeWindowBlock;
  }, [shouldBlock, blockBeforeUnload]);
};
